import type {
  Block,
  ArchDiagramRow,
  PipelineStage,
} from '../../types';

interface BlockRendererProps {
  block: Block;
}

export default function BlockRenderer({ block }: BlockRendererProps) {
  switch (block.type) {
    case 'heading':
      return <Heading level={block.level} text={block.text} id={block.id} />;
    case 'paragraph':
      return <p className="rich-paragraph">{block.text}</p>;
    case 'list':
      return <List ordered={block.ordered} items={block.items} />;
    case 'checklist':
      return <Checklist items={block.items} />;
    case 'alert':
      return <Alert variant={block.variant} title={block.title} body={block.body} />;
    case 'table':
      return <RichTable columns={block.columns} rows={block.rows} />;
    case 'step':
      return <Step number={block.number} title={block.title} bodyBlocks={block.bodyBlocks} />;
    case 'code':
      return <CodeBlock code={block.code} />;
    case 'badges':
      return <BadgeRow items={block.items} />;
    case 'pipelineFlow':
      return <PipelineFlow stages={block.stages} />;
    case 'patternBox':
      return (
        <PatternBox variant={block.variant} title={block.title} bodyBlocks={block.bodyBlocks} />
      );
    case 'archDiagram':
      return <ArchDiagram rows={block.rows} />;
    case 'card':
      return <Card {...block} />;
    case 'cardGrid':
      return (
        <div className="rich-card-grid">
          {block.cards.map((card, i) => (
            <Card key={i} {...card} />
          ))}
        </div>
      );
    case 'expandable':
      return (
        <Expandable
          summary={block.summary}
          subtitle={block.subtitle}
          tag={block.tag}
          defaultOpen={block.defaultOpen}
          bodyBlocks={block.bodyBlocks}
        />
      );
  }
}

function Expandable({
  summary,
  subtitle,
  tag,
  defaultOpen,
  bodyBlocks,
}: {
  summary: string;
  subtitle?: string;
  tag?: string;
  defaultOpen?: boolean;
  bodyBlocks: Block[];
}) {
  return (
    <details className="rich-expandable" {...(defaultOpen ? { open: true } : {})}>
      <summary className="rich-expandable-summary">
        <span className="rich-expandable-summary-marker" aria-hidden="true" />
        <span className="rich-expandable-summary-text">{summary}</span>
        {tag && <span className="rich-expandable-summary-tag">{tag}</span>}
        {subtitle && <span className="rich-expandable-summary-subtitle">{subtitle}</span>}
      </summary>
      <div className="rich-expandable-body">
        {bodyBlocks.map((b, i) => (
          <BlockRenderer key={i} block={b} />
        ))}
      </div>
    </details>
  );
}

function Heading({ level, text, id }: { level: 2 | 3 | 4; text: string; id?: string }) {
  if (level === 2) return <h2 id={id} className="rich-h2">{text}</h2>;
  if (level === 3) return <h3 id={id} className="rich-h3">{text}</h3>;
  return <h4 id={id} className="rich-h4">{text}</h4>;
}

function List({ ordered, items }: { ordered?: boolean; items: string[] }) {
  const Tag = ordered ? 'ol' : 'ul';
  return (
    <Tag className="rich-list">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </Tag>
  );
}

function Checklist({ items }: { items: { text: string; checked?: boolean }[] }) {
  return (
    <ul className="rich-checklist">
      {items.map((item, i) => (
        <li key={i} className={item.checked ? 'checked' : undefined}>
          <span className="rich-checklist-mark" aria-hidden="true" />
          <span>{item.text}</span>
        </li>
      ))}
    </ul>
  );
}

function Alert({
  variant,
  title,
  body,
}: {
  variant: 'success' | 'info' | 'warning' | 'danger';
  title?: string;
  body: string;
}) {
  return (
    <div className={`rich-alert rich-alert--${variant}`}>
      {title && <strong className="rich-alert-title">{title}</strong>}
      <span>{body}</span>
    </div>
  );
}

function RichTable({
  columns,
  rows,
}: {
  columns: string[];
  rows: Array<Array<string | { text: string; badge?: string; mono?: boolean }>>;
}) {
  return (
    <div className="rich-table-wrap">
      <table className="rich-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => {
                if (typeof cell === 'string') return <td key={ci}>{cell}</td>;
                if (cell.badge) {
                  return (
                    <td key={ci}>
                      <span className={`rich-badge rich-badge--${cell.badge}`}>{cell.text}</span>
                    </td>
                  );
                }
                if (cell.mono) {
                  return (
                    <td key={ci}>
                      <code className="rich-mono">{cell.text}</code>
                    </td>
                  );
                }
                return <td key={ci}>{cell.text}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Step({ number, title, bodyBlocks }: { number: number; title: string; bodyBlocks: Block[] }) {
  return (
    <div className="rich-step">
      <div className="rich-step-number" aria-hidden="true">
        {number}
      </div>
      <div className="rich-step-body">
        <h4 className="rich-step-title">{title}</h4>
        {bodyBlocks.map((b, i) => (
          <BlockRenderer key={i} block={b} />
        ))}
      </div>
    </div>
  );
}

function CodeBlock({ code }: { code: string }) {
  return (
    <pre className="rich-code">
      <code>{code}</code>
    </pre>
  );
}

function BadgeRow({ items }: { items: { text: string; variant?: string }[] }) {
  return (
    <div className="rich-badge-row">
      {items.map((b, i) => (
        <span key={i} className={`rich-badge rich-badge--${b.variant ?? 'info'}`}>
          {b.text}
        </span>
      ))}
    </div>
  );
}

function PipelineFlow({ stages }: { stages: PipelineStage[] }) {
  return (
    <div className="rich-pipeline-flow">
      {stages.map((stage, i) => (
        <span key={i} className="rich-pipeline-flow-row">
          <span className={`rich-pipeline-stage rich-pipeline-stage--${stage.variant ?? 'default'}`}>
            {stage.label}
          </span>
          {i < stages.length - 1 && <span className="rich-pipeline-arrow" aria-hidden="true">▶</span>}
        </span>
      ))}
    </div>
  );
}

function PatternBox({
  variant,
  title,
  bodyBlocks,
}: {
  variant?: 'accent' | 'default';
  title: string;
  bodyBlocks: Block[];
}) {
  return (
    <div className={`rich-pattern-box rich-pattern-box--${variant ?? 'default'}`}>
      <h4 className="rich-pattern-box-title">{title}</h4>
      <div className="rich-pattern-box-body">
        {bodyBlocks.map((b, i) => (
          <BlockRenderer key={i} block={b} />
        ))}
      </div>
    </div>
  );
}

function ArchDiagram({ rows }: { rows: ArchDiagramRow[] }) {
  return (
    <div className="rich-arch-diagram">
      {rows.map((row, ri) => (
        <div key={ri} className="rich-arch-row-wrap">
          {row.groupLabel && <div className="rich-arch-group-label">{row.groupLabel}</div>}
          {row.connectorAbove && (
            <div className="rich-arch-connector" aria-hidden="true">
              {row.connectorAbove}
            </div>
          )}
          <div className="rich-arch-row">
            {row.boxes.map((box, bi) => (
              <span key={bi} className="rich-arch-row-cell">
                <div className={`rich-arch-box rich-arch-box--${box.variant ?? 'default'}`}>
                  {box.label && <div className="rich-arch-box-label">{box.label}</div>}
                  <div className="rich-arch-box-name">{box.name}</div>
                  {box.detail && <div className="rich-arch-box-detail">{box.detail}</div>}
                </div>
                {bi < row.boxes.length - 1 && (
                  <span className="rich-arch-h-connector" aria-hidden="true">→</span>
                )}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Card({
  variant,
  title,
  tag,
  description,
  badges,
  operations,
  stats,
  href,
  download,
}: Extract<Block, { type: 'card' }>) {
  const headerClass = `rich-card-header rich-card-header--${variant ?? 'default'}`;
  const inner = (
    <>
      <header className={headerClass}>
        <h3 className="rich-card-title">{title}</h3>
        {tag && <span className="rich-card-tag">{tag}</span>}
      </header>
      <div className="rich-card-body">
        {badges && badges.length > 0 && (
          <div className="rich-badge-row">
            {badges.map((b, i) => (
              <span key={i} className={`rich-badge rich-badge--${b.variant ?? 'info'}`}>
                {b.text}
              </span>
            ))}
          </div>
        )}
        {description && <p className="rich-card-description">{description}</p>}
        {operations && operations.length > 0 && (
          <div className="rich-card-ops">
            {operations.map((op, i) => (
              <span key={i} className={`rich-op-badge rich-op-badge--${op.method}`}>
                {op.method} {op.path}
              </span>
            ))}
          </div>
        )}
        {stats && stats.length > 0 && (
          <div className="rich-card-stats">
            {stats.map((s, i) => (
              <span key={i} className="rich-card-stat">
                {s}
              </span>
            ))}
          </div>
        )}
        {download && (
          <a
            className="rich-card-download"
            href={download.href}
            download={download.filename}
            target="_blank"
            rel="noopener noreferrer"
          >
            {download.label ?? 'Download'}
          </a>
        )}
      </div>
    </>
  );
  if (href) {
    return (
      <a className="rich-card rich-card--link" href={href} target="_blank" rel="noopener noreferrer">
        {inner}
      </a>
    );
  }
  return <article className="rich-card">{inner}</article>;
}
